import { useState, useEffect } from "react"
import { ChevronDown, X, Search, Menu } from "lucide-react"
import styled from "styled-components"
import { Link } from "react-router-dom"

// === styled-components ===
const DropdownWrapper = styled.div`
  position: relative;
  &.group:hover .dropdown-content,
  &.group:focus-within .dropdown-content {
    display: block;
    pointer-events: auto;
  }
`

const DropdownContent = styled.div`
  display: none;
  position: absolute;
  top: calc(100% + 0.5rem);
  left: 0;
  background-color: white;
  min-width: 13rem;
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  box-shadow: 0px 8px 24px rgba(0, 0, 0, 0.06);
  z-index: 100; // 提高優先層級
  padding: 0.25rem 0;
  pointer-events: none;
`
const SubDropdown = styled.div`
  display: none;
  position: absolute;
  top: 0;
  left: 100%;
  background-color: white;
  min-width: 12rem;
  border: 1px solid #e2e8f0;
  z-index: 100;
  border-radius: 0.75rem;
  box-shadow: 0px 6px 12px rgba(0, 0, 0, 0.06);
  padding: 0.25rem 0;
  pointer-events: none;

  .group:hover > &,
  .group:focus-within > & {
    display: block;
    pointer-events: auto;
  }
`


const NavButton = styled.button`
  background: transparent;
  border: none;
  font-size: 0.9rem;
  padding: 0.5rem 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.4rem;
  color: #ffffff;
  font-weight: 500;
  border-radius: 0.5rem;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: #f1f5f9;
    color: #2563eb;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.06);
  }
`

const DropdownItem = styled.div`
  display: block;
  padding: 0.75rem 1rem;
  font-size: 0.875rem;
  color: #334155;
  font-weight: 400;
  cursor: pointer;
  white-space: nowrap;
  position: relative;
  transition: all 0.2s ease-in-out;
  &:hover {
    background-color: #f8fafc;
    color: #2563eb;
  }
`
const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  backdrop-filter: blur(2px);
  z-index: 50;
  transition: opacity 0.3s ease;
  &.hidden {
    opacity: 0;
    pointer-events: none;
  }
`

const Drawer = styled.div`
  position: fixed;
  top: 0;
  right: 0;
  height: 100%;
  width: 80%;
  max-width: 20rem;
  background: #ffffff;
  z-index: 60;
  border-left: 1px solid #e2e8f0;
  box-shadow: -4px 0 12px rgba(0, 0, 0, 0.06);
  transform: translateX(100%);
  transition: transform 0.35s ease-in-out;
  &.open {
    transform: translateX(0);
  }
`

const DesktopWrapper = styled.div`
  border-top: 1px solid #e5e7eb;
  background-color: #1e293b;
  display: none;
  @media (min-width: 768px) {
    display: block;
  }
`

const MobileToggle = styled.div`
  display: flex;
  justify-content: flex-end;
  padding: 0.5rem 1rem;
  background-color: #1e293b;
  border-bottom: 1px solid #e5e7eb;
  @media (min-width: 768px) {
    display: none;
  }
`

const NavContainer = styled.nav`
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1rem;
  display: flex;
  align-items: center;
  height: 60px;
  gap: 1.5rem;
`

const DrawerHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 1rem;
  border-bottom: 1px solid #e5e7eb;
`

const DrawerContent = styled.div`
  padding: 1rem;
`

const SearchWrapper = styled.div`
  position: relative;
  margin-bottom: 1rem;
  input {
    width: 100%;
    padding: 0.5rem 0.75rem;
    font-size: 0.875rem;
    border-radius: 0.375rem;
    border: 1px solid #d1d5db;
    outline: none;
  }
  svg {
    position: absolute;
    right: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: #6b7280;
    height: 1rem;
    width: 1rem;
  }
`

// === 遞迴展開用組件 ===

const RecursiveDropdown = ({ items, createAnchor }) => {
    return (
        <ul>
            {items.map((item, index) => (
                <DropdownItem className="group" key={index}>
                    {createAnchor(item, item.labelKey)}
                    {item.children && item.children.length > 0 && (
                        <SubDropdown>
                            <RecursiveDropdown items={item.children} createAnchor={createAnchor} />
                        </SubDropdown>
                    )}
                </DropdownItem>
            ))}
        </ul>
    )
}

// === 主元件 ===

const Navigation = ({ menuData = [] }) => {
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false)
    const [expandedPath, setExpandedPath] = useState([])
    const [currentPath, setCurrentPath] = useState("")

    useEffect(() => {
        setCurrentPath(window.location.pathname)
    }, [])

    const convertMenuDataToNavItems = (data) => {
        return data.map((item) => ({
            labelKey: item.label,
            href: item.key,
            pathCheck: item.path_check,
            children: item.children && item.children.length > 0 ? convertMenuDataToNavItems(item.children) : undefined,
        }))
    }

    const mainNavItems = convertMenuDataToNavItems(menuData)

    const createAnchor = (item, children, className = "") => {
        const isActive = item.href === currentPath
        const baseClass = `${className} ${isActive ? "font-semibold underline" : ""}`

        if (item.pathCheck === 0) {
            return (
                <Link to={item.href} className={baseClass} onClick={() => setMobileMenuOpen(false)}>
                    {children}
                </Link>
            )
        } else {
            return (
                <a href={item.href} target="_blank" rel="noopener noreferrer" className={baseClass} onClick={() => setMobileMenuOpen(false)}>
                    {children}
                </a>
            )
        }
    }

    const isExpanded = (level, index) => expandedPath[level] === index
    const toggleSubmenu = (level, index) => {
        const newPath = [...expandedPath.slice(0, level)]
        if (expandedPath[level] === index) {
            setExpandedPath(newPath)
        } else {
            newPath[level] = index
            setExpandedPath(newPath)
        }
    }

    const renderMobileMenu = (items, level = 0) => (
        <div className={`space-y-2 level-${level}`}>
            {items.map((item, index) => (
                <div key={`${level}-${index}`} className={`border-b py-2 level-${level}`}>
                    <div className="flex justify-between items-center font-medium text-base mb-1">
                        <div className="flex-1">
                            {createAnchor(item, (
                                <span className="block text-sm hover:bg-gray-100 px-2 py-1 rounded">
                                    {item.labelKey}
                                </span>
                            ))}
                        </div>
                        {item.children && item.children.length > 0 && (
                            <button
                                className="ml-2 text-gray-600"
                                onClick={() => toggleSubmenu(level, index)}
                                aria-label="展開選單"
                            >
                                <ChevronDown className={`h-4 w-4 transition-transform ${isExpanded(level, index) ? 'rotate-180' : ''}`} />
                            </button>
                        )}
                    </div>
                    {item.children && item.children.length > 0 && isExpanded(level, index) && (
                        <div className={`pl-4 mt-1 level-${level + 1}`}>
                            {renderMobileMenu(item.children, level + 1)}
                        </div>
                    )}
                </div>
            ))}
        </div>
    )

    return (
        <>
            <MobileToggle>
                <button style={{ color: '#ffffff' }} onClick={() => setMobileMenuOpen(true)} aria-label="開啟選單">
                    <Menu style={{ color: '#ffffff' }} className="h-6 w-6 text-gray-800" />
                </button>
            </MobileToggle>

            <DesktopWrapper>
                <NavContainer>
                    {mainNavItems.map((item, index) => (
                        <DropdownWrapper className="group" key={index}>
                            <NavButton
                                aria-haspopup={item.children && item.children.length > 0 ? "true" : undefined}
                                aria-expanded={item.children && item.children.length > 0 ? "false" : undefined}
                            >
                                {createAnchor(item, item.labelKey)}
                                {item.children && item.children.length > 0 && <ChevronDown className="h-4 w-4 transition-transform" />}
                            </NavButton>
                            {item.children && item.children.length > 0 && (
                                <DropdownContent className="dropdown-content">
                                    <RecursiveDropdown items={item.children} createAnchor={createAnchor} />
                                </DropdownContent>
                            )}
                        </DropdownWrapper>
                    ))}
                </NavContainer>
            </DesktopWrapper>

            <Backdrop className={!mobileMenuOpen ? "hidden" : ""} onClick={() => setMobileMenuOpen(false)}>
                <Drawer className={mobileMenuOpen ? "open" : ""} onClick={(e) => e.stopPropagation()}>
                    <DrawerHeader>
                        <button onClick={() => setMobileMenuOpen(false)} aria-label="關閉">
                            <X className="h-5 w-5" />
                        </button>
                    </DrawerHeader>
                    <DrawerContent>
                        {renderMobileMenu(mainNavItems)}
                    </DrawerContent>
                </Drawer>
            </Backdrop>
        </>
    )
}

export default Navigation
